// src/components/pages/portfolios/partials/PortfolioCompositionHistory.js

import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from 'recharts';
import styles from './../style/PortfolioDetail.module.css';

const COLORS = [
  '#e6194b', '#3cb44b', '#ffe119', '#0082c8', '#f58231', '#911eb4', '#46f0f0', '#f032e6',
  '#d2f53c', '#fabebe', '#008080', '#e6beff', '#aa6e28', '#fffac8', '#800000', '#aaffc3',
  '#808000', '#ffd8b1', '#000080', '#808080', '#ffffff', '#000000', '#ff7f00', '#984ea3'
];

// Top N 개수 설정 (여기서는 10개)
const TOP_N = 10;
const OTHERS_TICKER = 'Others';
const OTHERS_COLOR = '#808080'; // 기타 항목을 위한 회색

export default function PortfolioCompositionHistory({ timeSeries, t }) {
  const [showCompositionHistory, setShowCompositionHistory] = useState(false);
  const compositionHistory = timeSeries?.compositionHistory || [];

  const { displayTickers, processedHistory } = useMemo(() => {
    if (compositionHistory.length === 0) {
      return { displayTickers: [], processedHistory: [] };
    }

    // 1. 마지막 시점 비중을 기준으로 모든 티커의 현재 비중 계산
    const lastComp = compositionHistory[compositionHistory.length - 1] || {};
    const allTickers = Object.keys(lastComp).filter(k => k !== 'date');
    
    // 2. 현재 비중이 높은 순서로 정렬하여 Top N 선정
    const sortedTickers = allTickers
      .map(ticker => ({ ticker, weight: lastComp[ticker] ?? 0 }))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, TOP_N)
      .map(d => d.ticker);

    // 표시할 티커 목록 (Top N + Others)
    const displayTickers = [...sortedTickers, OTHERS_TICKER];
    
    // Top N에 포함되지 않은 티커 목록
    const otherTickers = allTickers.filter(ticker => !sortedTickers.includes(ticker));

    // 3. 히스토리 데이터 가공 (Top N은 그대로, 나머지는 Others로 합산)
    const processedHistory = compositionHistory.map(row => {
      const newRow = { date: row.date };
      let othersWeight = 0;

      allTickers.forEach(ticker => {
        const weight = row[ticker] ?? 0; // 데이터가 없을 경우 0으로 처리

        if (sortedTickers.includes(ticker)) {
          // Top N 종목은 개별 키로 유지
          newRow[ticker] = weight;
        } else {
          // 나머지 종목은 Others에 합산
          othersWeight += weight;
        }
      });

      // Others 비중 추가
      if (othersWeight > 0 || otherTickers.length > 0) {
        newRow[OTHERS_TICKER] = othersWeight;
      }
      return newRow;
    });

    return { displayTickers, processedHistory };
  }, [compositionHistory]);

  if (compositionHistory.length === 0) return null;

  // Others를 항상 마지막에 위치시키기 위해 COLORS 배열을 이용해 인덱스 조정
  const getTickerColor = (ticker, idx) => {
    if (ticker === OTHERS_TICKER) {
      return OTHERS_COLOR;
    }
    // Others를 제외한 티커들만으로 순서대로 COLORS 배열을 사용
    const baseIdx = displayTickers.slice(0, -1).indexOf(ticker);
    return COLORS[baseIdx % COLORS.length];
  };

  return (
    <div className={styles.toggleSection}>
      {/* <button onClick={() => setShowCompositionHistory(!showCompositionHistory)} className={styles.infoButton}>
        <span>{showCompositionHistory ? t('portfolioDetail.compositionHistoryToggle.close') : t('portfolioDetail.compositionHistoryToggle.open')}</span>
        <span className={`${styles.infoIconToggle} ${showCompositionHistory ? styles.open : ''}`}>▼</span>
      </button> */}
      {/* {showCompositionHistory && ( */}
      { (
        <div className={styles.chartBlock}>
          <h3 className={styles.subTitle}>{t('portfolioDetail.compositionHistoryTitle')}</h3>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={processedHistory} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis tickFormatter={v => `${(v * 100).toFixed(0)}%`} width={60} domain={[0, 1]} />
              <Tooltip formatter={v => `${(v * 100).toFixed(2)}%`} />
              <Legend />
              {/* displayTickers를 사용하여 Area 컴포넌트 렌더링 */}
              {displayTickers.map((ticker) => (
                <Area key={ticker} type="monotone" dataKey={ticker} stackId="1" 
                      stroke={getTickerColor(ticker)} fill={getTickerColor(ticker)} fillOpacity={0.6} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}